/**
 * orderFlow — the pure logic behind the ordering screen and checkout.
 *
 * Kept free of React so it can be unit-tested: checkout-attempt keys (so a
 * retry never double-orders), pickup-time choices, the order-tracking stages,
 * what to feature at the top of the menu, and the mid-checkout snapshot that
 * lets a payment survive a redirect or a refresh.
 */

// ─── Checkout attempt keys ───────────────────────────────────────────────────

function uuid() {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* fall through */ }
  return `k${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * One idempotency key per distinct checkout attempt.
 *
 * `keyFor(payload)` returns the same { idempotencyKey, requestedAt } for as long
 * as the payload is unchanged — so tapping twice, or retrying after the
 * connection dropped, asks the server for the SAME order. Change anything
 * (cart, name, pickup time, pay method) and it's a new attempt with a new key.
 * `clear()` after a finished order so ordering the same thing again is new.
 */
export function createAttemptKeys(seed = null) {
  let current = seed; // { sig, idempotencyKey, requestedAt }
  return {
    keyFor(payload) {
      const sig = JSON.stringify(payload);
      if (!current || current.sig !== sig) {
        current = { sig, idempotencyKey: uuid(), requestedAt: new Date().toISOString() };
      }
      return { idempotencyKey: current.idempotencyKey, requestedAt: current.requestedAt };
    },
    clear() { current = null; },
  };
}

// ─── Pickup time ─────────────────────────────────────────────────────────────

export const PICKUP_CHOICES = [
  { id: 'asap', label: 'ASAP', hint: '~20 min', minutes: 0 },
  { id: '30', label: '30 min', minutes: 30 },
  { id: '45', label: '45 min', minutes: 45 },
  { id: '60', label: '1 hour', minutes: 60 },
  { id: 'custom', label: 'Pick a time', minutes: null },
];

/**
 * The pickup time to send for a choice: null for ASAP (the server times it),
 * else a full ISO instant. A custom `datetime-local` value has no timezone, so
 * it is resolved in the customer's own timezone here — sent raw, the server
 * would read it as UTC and schedule the pickup hours off.
 */
export function pickupIso(choiceId, customValue, now = new Date()) {
  const choice = PICKUP_CHOICES.find(c => c.id === choiceId);
  if (!choice || choice.id === 'asap') return null;
  if (choice.id === 'custom') {
    const d = customValue ? new Date(customValue) : null;
    return d && !isNaN(d.getTime()) ? d.toISOString() : null;
  }
  return new Date(now.getTime() + choice.minutes * 60 * 1000).toISOString();
}

/** "6:45 PM", or "Tue 6:45 PM" when it isn't today. */
export function pickupLabel(iso, now = new Date()) {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === now.toDateString()
    ? time
    : `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
}

// ─── Contact ─────────────────────────────────────────────────────────────────

/** Format a US phone number as it is typed: "(479) 555-1234". */
export function formatPhoneInput(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Loose US phone check (the server re-validates and normalizes to E.164). */
export function isValidPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 && ten[0] !== '0' && ten[0] !== '1';
}

// ─── Cart → request ──────────────────────────────────────────────────────────

/**
 * The cart as the order endpoint wants it. Prices ride along for display only —
 * the server re-prices every line from the Square catalog. `resolveSquareName`
 * maps a legacy name-only line (added from /menu) to its Square catalog name so
 * the server can find it.
 */
export function cartToItems(items, resolveSquareName) {
  return (items || []).map(it => ({
    name: it.name,
    squareName: (!it.catalogObjectId && resolveSquareName?.(it.name)) || it.squareName || it.name,
    catalogObjectId: it.catalogObjectId || null,
    variationId: it.variationId || null,
    variationName: it.variationName || null,
    basePrice: it.basePrice != null ? it.basePrice : it.price,
    modifiers: (it.modifiers || []).map(m => ({ id: m.id || null, name: m.name, priceCents: m.priceCents || 0, ...(m.custom ? { custom: true } : {}) })),
    qty: it.qty,
    note: it.note || '',
  }));
}

/** Cart subtotal in cents, as the browser believes it. */
export function cartSubtotalCents(items) {
  return (items || []).reduce((s, it) =>
    s + it.qty * Math.round((Number(it.unitPrice ?? it.basePrice ?? it.price) || 0) * 100), 0);
}

/**
 * Did Square price the order differently from what the cart showed? True when a
 * price moved after the item was added — the checkout says so before charging.
 */
export function quoteDiffers(cartItems, quote) {
  if (!quote?.totalMoney) return false;
  const squareSubtotal = (quote.totalMoney.amount || 0) - (quote.taxMoney?.amount || 0);
  return squareSubtotal !== cartSubtotalCents(cartItems);
}

/**
 * Bring a saved cart up to date with the live menu.
 *
 * A cart can sit in the browser for days. Lines whose item (or chosen size or
 * option) is gone, hidden or sold out are dropped and named in `removed`;
 * lines whose price moved are re-priced (`repriced`). Legacy name-only lines
 * are left alone — the server prices those at checkout.
 * `items` is the SAME array when nothing changed, so callers can skip a write.
 */
export function reconcileCart(cartItems, categories) {
  const byId = new Map();
  (categories || []).forEach(c => (c.items || []).forEach(i => byId.set(String(i.id), i)));
  const removed = [];
  let repriced = false;

  const items = (cartItems || []).map((line) => {
    if (!line.catalogObjectId) return line;
    const item = byId.get(String(line.catalogObjectId));
    const variation = item && (item.variations || []).find(v => v.id === line.variationId);
    if (!item || item.ecomVisible === false || item.soldOut || !variation || variation.soldOut) {
      removed.push(line.name);
      return null;
    }

    const squareMods = (item.modifierLists || []).flatMap(l => l.modifiers || []);
    const customOpts = (item.optionGroups || []).flatMap(g => g.options || []);
    let optionGone = false;
    const modifiers = (line.modifiers || []).map((m) => {
      const id = String(m.id || '');
      const now = id.startsWith('custom:')
        ? customOpts.find(o => `custom:${o.id}` === id)
        : squareMods.find(o => String(o.id) === id);
      if (!now) { optionGone = true; return m; }
      return { ...m, name: now.name, priceCents: now.priceCents || 0 };
    });
    if (optionGone) { removed.push(line.name); return null; }

    const basePrice = (variation.priceCents || 0) / 100;
    const unitCents = (variation.priceCents || 0) + modifiers.reduce((s, m) => s + (m.priceCents || 0), 0);
    if (unitCents === Math.round(Number(line.unitPrice) * 100)) return line;
    repriced = true;
    return { ...line, basePrice, modifiers, unitPrice: unitCents / 100 };
  }).filter(Boolean);

  return { items: removed.length || repriced ? items : cartItems, repriced, removed };
}

// ─── Order tracking ──────────────────────────────────────────────────────────

export const TRACK_STEPS = [
  { stage: 'received', label: 'Received', detail: 'We have your order.' },
  { stage: 'preparing', label: 'Preparing', detail: 'The kitchen is cooking it now.' },
  { stage: 'ready', label: 'Ready', detail: 'Come to the pickup window.' },
];

/** Index of the step a stage has reached (picked up = past the last step). */
export function stageIndex(stage) {
  if (stage === 'picked_up') return TRACK_STEPS.length;
  const i = TRACK_STEPS.findIndex(s => s.stage === stage);
  return i < 0 ? 0 : i;
}

/** Nothing more will happen to the order — stop polling. */
export function isFinalStage(stage) {
  return stage === 'picked_up' || stage === 'canceled';
}

const TRACK_WINDOW_MS = 3 * 60 * 60 * 1000;

/** The most recent order on this device still worth tracking (placed < 3 h ago). */
export function activeOrder(history, now = Date.now()) {
  const o = (history || [])[0];
  if (!o?.orderId || o.done) return null;
  const placed = new Date(o.placedAt).getTime();
  return Number.isFinite(placed) && now - placed < TRACK_WINDOW_MS ? o : null;
}

// ─── Menu presentation ───────────────────────────────────────────────────────

const FEATURE_BADGES = ['popular', 'bestseller', 'chef'];

/**
 * What to show in the "Popular" rail at the top of the menu: items a manager
 * badged as popular / best seller / chef's pick first, then items that have a
 * photo. In stock only, each item once.
 */
export function featuredItems(categories, n = 10) {
  const all = (categories || []).flatMap(c => c.items || []).filter(i => !i.soldOut);
  const badged = all.filter(i => (i.badges || []).some(b => FEATURE_BADGES.includes(b)));
  const withPhoto = all.filter(i => i.imageUrl && !badged.includes(i));
  const seen = new Set();
  return [...badged, ...withPhoto].filter(i => (seen.has(i.id) ? false : seen.add(i.id))).slice(0, n);
}

/** Lowest in-stock price and whether the price varies ("from $x"). */
export function priceOf(item) {
  const vars = item.variations || [];
  const pool = vars.filter(v => !v.soldOut).length ? vars.filter(v => !v.soldOut) : vars;
  const prices = pool.map(v => v.priceCents);
  const cents = prices.length ? Math.min(...prices) : (item.basePriceCents || 0);
  return { cents, from: new Set(prices).size > 1 };
}

/** Case-insensitive match of every search word against name + description. */
export function matchesQuery(item, query) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${item.name} ${item.description || ''}`.toLowerCase();
  return words.every(w => hay.includes(w));
}

// ─── Mid-checkout snapshot ───────────────────────────────────────────────────

const PENDING_KEY = 'db_order_pending_v1';
const PENDING_MAX_AGE_MS = 20 * 60 * 1000;

/**
 * Remember a quoted, unpaid order for this tab. Wallets such as Cash App send
 * the buyer away and back, and people refresh — either way the checkout reopens
 * on the SAME Square order instead of quietly creating another.
 * sessionStorage: gone when the tab closes, never shared between tabs.
 */
export function savePending(pending) {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, savedAt: Date.now() })); } catch { /* private mode */ }
}

export function loadPending(now = Date.now()) {
  try {
    const p = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    if (!p?.quote?.orderId || now - (p.savedAt || 0) > PENDING_MAX_AGE_MS) return null;
    return p;
  } catch { return null; }
}

export function clearPending() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}
